import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { authApi } from "@/services/api/auth";
import { useAuth } from "@/store/authStore";
import { UserData } from "@/services/types/domain";

// Estados posibles del flujo de verificación
type ViewState = "loading" | "success" | "already" | "invalid" | "error";

const VerifyEmailPage: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const auth = (useAuth as any)?.();

    const [state, setState] = useState<ViewState>("loading");
    const [message, setMessage] = useState<string>("");
    const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
    //const [resending, setResending] = useState(false);

    const token = params.get("token");

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!token) {
                setState("invalid");
                setMessage("Falta el token de verificación en la URL.");
                return;
            }
            try {
                setState("loading");
                // POST /auth/verify-email { token }
                const updated: UserData = await (authApi as any).verifyEmail(token);
                // Opcionalmente refresca el perfil para asegurar datos frescos
                const fresh: UserData = (authApi as any).getUserProfile
                    ? await (authApi as any).getUserProfile()
                    : updated;
                if (!mounted) return;
                auth?.setUser?.(fresh);
                setVerifiedAt(fresh.email_verified_at ?? new Date().toISOString());
                setMessage("Tu correo ha sido verificado correctamente.");
                setState("success");
            } catch (err: any) {
                if (!mounted) return;
                const code = err?.response?.data?.code ?? err?.response?.status;
                const apiError = err?.response?.data?.error || err?.message;
                if (code === 409) {
                    // { error: "Este correo electrónico ya ha sido verificado previamente.", code: 409 }
                    setState("already");
                    setMessage(apiError || "Este correo electrónico ya fue verificado.");
                    try {
                        if ((authApi as any).getUserProfile) {
                            const fresh: UserData = await (authApi as any).getUserProfile();
                            auth?.setUser?.(fresh);
                            setVerifiedAt(fresh.email_verified_at ?? null);
                        }
                    } catch { }
                } else if (code === 400) {
                    // { error: "El token de verificación es inválido.", code: 400 }
                    setState("invalid");
                    setMessage(apiError || "El token de verificación es inválido o ha expirado.");
                } else {
                    setState("error");
                    setMessage(apiError || "No se pudo verificar el correo.");
                }
            }
        })();
        return () => { mounted = false; };
    }, [token]);

    /*const handleResend = async () => {
        try {
            setResending(true);
            await (authApi as any).sendVerificationEmail(); // POST /auth/send-verification-email
            setMessage("Te enviamos un nuevo correo con el enlace de verificación. Revisa tu bandeja.");
        } catch (err: any) {
            setMessage(err?.response?.data?.error || err?.message || "No se pudo reenviar el correo de verificación.");
        } finally {
            setResending(false);
        }
    };*/

    const handleRetry = () => {
        // Fuerza un reintento manteniendo el mismo token
        setState("loading");
        setMessage("");
        // Dispara de nuevo el efecto cambiando una clave artificial (o usa navigate(0))
        navigate(0);
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8">
            <div className="mx-auto w-full max-w-lg">
                <div className="rounded-2xl border bg-white p-8 text-center shadow-sm">
                    {state === "loading" && (
                        <div className="text-gray-600">Verificando correo…</div>
                    )}

                    {state === "success" && (
                        <>
                            <div className="mb-2 text-2xl font-bold text-gray-900">¡Correo verificado!</div>
                            <p className="mb-6 text-gray-600">{message}</p>
                            {verifiedAt && (
                                <div className="mb-6">
                                    <Badge variant="success">Verificado el {new Date(verifiedAt).toLocaleString()}</Badge>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                                <Button onClick={() => navigate("/profile")}>Ir a mi perfil</Button>
                                <Button onClick={() => navigate("/")}>Ir al inicio</Button>
                            </div>
                        </>
                    )}

                    {state === "already" && (
                        <>
                            <div className="mb-2 text-2xl font-bold text-gray-900">Correo ya verificado</div>
                            <p className="mb-6 text-gray-600">{message || "Este correo electrónico ya se encontraba verificado."}</p>
                            {verifiedAt && (
                                <div className="mb-6">
                                    <Badge variant="secondary">Verificado el {new Date(verifiedAt).toLocaleString()}</Badge>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                                <Button onClick={() => navigate("/profile")}>Ir a mi perfil</Button>
                                <Button onClick={() => navigate("/")}>Ir al inicio</Button>
                            </div>
                        </>
                    )}

                    {state === "invalid" && (
                        <>
                            <div className="mb-2 text-2xl font-bold text-gray-900">Enlace inválido o expirado</div>
                            <p className="mb-6 text-gray-600">{message || "El token de verificación es inválido o expiró. Puedes solicitar un nuevo correo de verificación."}</p>
                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                                {/* <Button onClick={handleResend} disabled={resending}>
                                    {resending ? "Enviando…" : "Reenviar correo de verificación"}
                                </Button> */}
                                <Button onClick={() => navigate("/profile")} variant="secondary">
                                    Volver al perfil
                                </Button>
                            </div>
                        </>
                    )}

                    {state === "error" && (
                        <>
                            <div className="mb-2 text-2xl font-bold text-gray-900">No se pudo verificar</div>
                            <p className="mb-6 text-gray-600">{message}</p>
                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                                <Button onClick={handleRetry}>Reintentar</Button>
                                <Button onClick={() => navigate("/profile")} variant="secondary">
                                    Volver al perfil
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailPage;
